<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useFunnelManager } from 'dashboard/composables/kanban/useFunnelManager';
import Button from 'dashboard/components-next/button/Button.vue';
import { FUNNEL_TEMPLATES } from 'dashboard/constants/funnelTemplates';

const store = useStore();
const router = useRouter();
const {
  funnels,
  loadFunnels,
  createFunnel,
  updateFunnel,
  deleteFunnel,
  createFunnelFromTemplateId,
} = useFunnelManager();

// Estado local
const showCreateModal = ref(false);
const showEditModal = ref(false);
const selectedFunnel = ref(null);
const selectedTemplate = ref('');
const activeDropdown = ref(null);

// Estado do funil customizado
const customFunnel = ref({
  name: '',
  description: '',
  stages: [],
});

const newStageName = ref('');
const newStageColor = ref('#6366F1');
const newStageDescription = ref('');
const editingStageIndex = ref(null);

// Usar templates importados
const funnelTemplates = FUNNEL_TEMPLATES.map(template => ({
  type: template.id,
  name: template.name,
  icon: template.settings.icon,
  description: template.description,
  stages: template.stages,
}));

// Pre-defined colors for stages
const stageColors = [
  '#6366F1', // Indigo
  '#8B5CF6', // Violet
  '#EC4899', // Pink
  '#EF4444', // Red
  '#F59E0B', // Amber
  '#10B981', // Emerald
  '#06B6D4', // Cyan
  '#3B82F6', // Blue
  '#14B8A6', // Teal
  '#F97316', // Orange
];

// Methods
const addStage = () => {
  if (!newStageName.value.trim()) return;

  const stage = {
    label: newStageName.value.trim(),
    color: newStageColor.value,
    description: newStageDescription.value.trim(),
    position: customFunnel.value.stages.length,
  };

  if (editingStageIndex.value !== null) {
    customFunnel.value.stages[editingStageIndex.value] = stage;
    editingStageIndex.value = null;
  } else {
    customFunnel.value.stages.push(stage);
  }

  newStageName.value = '';
  newStageDescription.value = '';
  newStageColor.value =
    stageColors[customFunnel.value.stages.length % stageColors.length];
};

const editStage = index => {
  const stage = customFunnel.value.stages[index];
  newStageName.value = stage.label;
  newStageColor.value = stage.color;
  newStageDescription.value = stage.description || '';
  editingStageIndex.value = index;
};

const removeStage = index => {
  customFunnel.value.stages.splice(index, 1);
  customFunnel.value.stages.forEach((stage, i) => {
    stage.position = i;
  });
};

const handleCreateFunnel = async () => {
  let funnelData;

  if (selectedTemplate.value) {
    const customName = customFunnel.value.name || null;
    const customSettings = {
      agent_ids: [],
      team_ids: [],
    };

    try {
      await createFunnelFromTemplateId(
        selectedTemplate.value,
        customName,
        customSettings
      );

      showCreateModal.value = false;
      resetForm();
      await loadFunnels();
      return;
    } catch (error) {
      alert('Erro ao criar funil. Tente novamente.');
      return;
    }
  } else {
    if (!customFunnel.value.name) {
      alert('Por favor, adicione um nome para o funil');
      return;
    }

    if (customFunnel.value.stages.length === 0) {
      alert('Por favor, adicione pelo menos uma etapa');
      return;
    }

    const stagesObj = {};
    customFunnel.value.stages.forEach((stage, index) => {
      const key = stage.label.toLowerCase().replace(/\s+/g, '_');
      stagesObj[key] = {
        ...stage,
        position: index,
        allow_new_conversations: index === 0,
      };
    });

    funnelData = {
      name: customFunnel.value.name,
      description: customFunnel.value.description,
      funnel_type: 'custom',
      stages: stagesObj,
      settings: {
        icon: '📊',
        agent_ids: [],
        team_ids: [],
        auto_assign: false,
        default_stage: Object.keys(stagesObj)[0],
        auto_move: false,
        notify_on_stage_change: true,
        allow_manual_move: true,
        enable_sla: false,
        enable_automation: false,
        conversation_limit: 0,
      },
    };
  }

  await createFunnel({ funnel: funnelData });

  showCreateModal.value = false;
  resetForm();
  await loadFunnels();
};

const resetForm = () => {
  customFunnel.value = {
    name: '',
    description: '',
    stages: [],
  };
  newStageName.value = '';
  newStageDescription.value = '';
  newStageColor.value = '#6366F1';
  editingStageIndex.value = null;
  selectedTemplate.value = '';
};

const selectTemplate = templateType => {
  selectedTemplate.value = templateType;
  const template = funnelTemplates.find(t => t.type === templateType);
  if (template) {
    customFunnel.value.name = template.name;
    customFunnel.value.description = template.description;
  }
};

const openCreateModal = () => {
  resetForm();
  showCreateModal.value = true;
};

const handleEditFunnel = async () => {
  if (!selectedFunnel.value) return;

  try {
    if (
      !selectedFunnel.value.stages ||
      Object.keys(selectedFunnel.value.stages).length === 0
    ) {
      alert('O funil deve ter pelo menos uma etapa!');
      return;
    }

    await updateFunnel(selectedFunnel.value.id, {
      funnel: {
        name: selectedFunnel.value.name,
        description: selectedFunnel.value.description,
        stages: selectedFunnel.value.stages,
        settings: selectedFunnel.value.settings || {},
      },
    });

    showEditModal.value = false;
    selectedFunnel.value = null;
    await loadFunnels();
    alert('Funil atualizado com sucesso!');
  } catch (error) {
    alert('Erro ao atualizar funil. Tente novamente.');
  }
};

const handleDeleteFunnel = async funnelId => {
  if (confirm('Tem certeza que deseja excluir este funil?')) {
    await deleteFunnel(funnelId);
    await loadFunnels();
  }
};

const goToFunnelSettings = funnel => {
  const accountId = store.getters.getCurrentAccountId;
  router.push({
    name: 'funnel_settings',
    params: {
      accountId: accountId,
      funnelId: funnel.id,
    },
  });
};

const openEditModal = funnel => {
  selectedFunnel.value = JSON.parse(JSON.stringify(funnel));
  showEditModal.value = true;
};

const addStageToSelectedFunnel = () => {
  if (!selectedFunnel.value) return;

  const newKey = `stage_${Date.now()}`;
  const stageCount = Object.keys(selectedFunnel.value.stages || {}).length;

  if (!selectedFunnel.value.stages) {
    selectedFunnel.value.stages = {};
  }

  selectedFunnel.value.stages[newKey] = {
    label: `Nova Etapa ${stageCount + 1}`,
    color: stageColors[stageCount % stageColors.length],
    position: stageCount,
    description: '',
    sla_hours: 24,
  };
};

const removeStageFromSelectedFunnel = stageKey => {
  if (!selectedFunnel.value || !selectedFunnel.value.stages) return;

  const stageKeys = Object.keys(selectedFunnel.value.stages);
  if (stageKeys.length <= 1) {
    alert('O funil deve ter pelo menos uma etapa!');
    return;
  }

  if (confirm('Tem certeza que deseja remover esta etapa?')) {
    delete selectedFunnel.value.stages[stageKey];

    let position = 0;
    Object.keys(selectedFunnel.value.stages)
      .sort(
        (a, b) =>
          selectedFunnel.value.stages[a].position -
          selectedFunnel.value.stages[b].position
      )
      .forEach(key => {
        selectedFunnel.value.stages[key].position = position++;
      });
  }
};

const handleClickOutside = event => {
  if (!event.target.closest('.relative')) {
    activeDropdown.value = null;
  }
};

const goBackToKanban = () => {
  router.push({ name: 'kanban_view' });
};

// Lifecycle
onMounted(() => {
  loadFunnels();
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div class="flex flex-col h-full w-full bg-n-background">
    <!-- Header -->
    <div class="flex items-center justify-between px-6 py-5 border-b border-n-weak">
      <div class="flex items-center gap-4">
        <button
          class="size-9 rounded-lg flex items-center justify-center text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
          title="Voltar para o Kanban"
          @click="goBackToKanban"
        >
          <span class="i-lucide-arrow-left size-5" />
        </button>
        <div>
          <h1 class="text-lg font-medium text-n-slate-12">Gerenciar Funis</h1>
          <p class="text-sm text-n-slate-11 mt-0.5">
            Crie e gerencie funis de vendas e atendimento
          </p>
        </div>
      </div>
      <Button icon="i-lucide-plus" @click="openCreateModal">
        Novo Funil
      </Button>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto p-6">
      <!-- Empty State -->
      <div
        v-if="funnels.length === 0"
        class="flex flex-col items-center justify-center py-16"
      >
        <span class="i-lucide-kanban size-16 text-n-slate-10 opacity-50" />
        <p class="mt-4 text-sm text-n-slate-11">Nenhum funil criado ainda</p>
        <Button
          label="Criar primeiro funil"
          variant="smooth"
          class="mt-4"
          @click="openCreateModal"
        />
      </div>

      <!-- Funnel Grid -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          v-for="funnel in funnels"
          :key="funnel.id"
          class="flex flex-col p-4 rounded-xl bg-n-solid-2 outline outline-1 outline-n-container hover:shadow-lg transition-shadow"
        >
          <!-- Header -->
          <div class="flex items-start justify-between mb-3">
            <div class="flex items-center gap-3">
              <span class="text-2xl">{{ funnel.settings?.icon || '📊' }}</span>
              <h3 class="text-base font-medium text-n-slate-12">
                {{ funnel.name }}
              </h3>
            </div>

            <!-- Dropdown Menu -->
            <div class="relative">
              <button
                class="size-8 rounded-lg flex items-center justify-center text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
                @click.stop="
                  activeDropdown = activeDropdown === funnel.id ? null : funnel.id
                "
              >
                <span class="i-lucide-more-vertical size-4" />
              </button>

              <div
                v-if="activeDropdown === funnel.id"
                class="absolute right-0 top-full mt-1 w-48 rounded-xl bg-n-solid-2 shadow-xl outline outline-1 outline-n-container z-50 py-1 overflow-hidden"
              >
                <button
                  class="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors text-left"
                  @click.stop="
                    openEditModal(funnel);
                    activeDropdown = null;
                  "
                >
                  <span class="i-lucide-pencil size-4 text-n-slate-11" />
                  Editar
                </button>
                <button
                  class="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors text-left"
                  @click.stop="
                    goToFunnelSettings(funnel);
                    activeDropdown = null;
                  "
                >
                  <span class="i-lucide-settings size-4 text-n-slate-11" />
                  Configurações
                </button>
                <button
                  class="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-ruby-600 hover:bg-ruby-500/10 transition-colors text-left"
                  @click.stop="
                    handleDeleteFunnel(funnel.id);
                    activeDropdown = null;
                  "
                >
                  <span class="i-lucide-trash-2 size-4" />
                  Excluir
                </button>
              </div>
            </div>
          </div>

          <!-- Description -->
          <p class="text-sm text-n-slate-11 mb-4 line-clamp-2">
            {{ funnel.description }}
          </p>

          <!-- Stages -->
          <div class="flex flex-wrap gap-1.5 mt-auto">
            <span
              v-for="(stage, key) in funnel.stages"
              :key="key"
              class="px-2 py-1 rounded-lg text-xs font-medium"
              :style="{
                backgroundColor: stage.color + '20',
                color: stage.color,
              }"
            >
              {{ stage.label }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Modal -->
    <Teleport to="body">
      <div
        v-if="showCreateModal"
        class="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      >
        <div
          class="absolute inset-0 bg-n-alpha-black5 backdrop-blur-sm"
          @click="
            showCreateModal = false;
            resetForm();
          "
        />

        <div
          class="relative w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-2xl bg-n-solid-2 shadow-xl outline outline-1 outline-n-container flex flex-col"
        >
          <!-- Header -->
          <div
            class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
          >
            <h2 class="text-base font-medium text-n-slate-12">Novo Funil</h2>
            <button
              class="size-8 rounded-lg flex items-center justify-center text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
              @click="
                showCreateModal = false;
                resetForm();
              "
            >
              <span class="i-lucide-x size-5" />
            </button>
          </div>

          <!-- Content -->
          <div class="flex-1 overflow-y-auto p-6">
            <div class="grid grid-cols-3 gap-6">
              <!-- Left: Form -->
              <div class="col-span-2 flex flex-col gap-6">
                <!-- Name & Description -->
                <div class="flex flex-col gap-4">
                  <div class="flex flex-col gap-1.5">
                    <label class="text-sm font-medium text-n-slate-11">Nome</label>
                    <input
                      v-model="customFunnel.name"
                      type="text"
                      placeholder="Digite o nome do funil"
                      class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm placeholder:text-n-slate-10 focus:border-woot-500 focus:outline-none transition-colors"
                    />
                  </div>
                  <div class="flex flex-col gap-1.5">
                    <label class="text-sm font-medium text-n-slate-11">Descrição</label>
                    <textarea
                      v-model="customFunnel.description"
                      rows="2"
                      placeholder="Digite a descrição do funil"
                      class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm placeholder:text-n-slate-10 focus:border-woot-500 focus:outline-none transition-colors resize-none"
                    />
                  </div>
                </div>

                <!-- Add Stage Form -->
                <div
                  class="p-4 rounded-xl bg-n-alpha-1 outline outline-1 outline-n-container"
                >
                  <h3 class="text-sm font-medium text-n-slate-12 mb-4">
                    Adicionar Etapa
                  </h3>
                  <div class="flex flex-col gap-3">
                    <div class="flex flex-col gap-1.5">
                      <label class="text-xs font-medium text-n-slate-11">Nome</label>
                      <input
                        v-model="newStageName"
                        type="text"
                        placeholder="Digite o nome da etapa"
                        class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm placeholder:text-n-slate-10 focus:border-woot-500 focus:outline-none transition-colors"
                        @keyup.enter="addStage"
                      />
                    </div>
                    <div class="grid grid-cols-2 gap-3">
                      <div class="flex flex-col gap-1.5">
                        <label class="text-xs font-medium text-n-slate-11">Cor</label>
                        <div class="flex items-center gap-2">
                          <input
                            v-model="newStageColor"
                            type="color"
                            class="size-9 rounded-lg border border-n-weak cursor-pointer"
                          />
                          <input
                            v-model="newStageColor"
                            type="text"
                            class="flex-1 h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-xs font-mono focus:border-woot-500 focus:outline-none transition-colors"
                          />
                        </div>
                      </div>
                      <div class="flex flex-col gap-1.5">
                        <label class="text-xs font-medium text-n-slate-11">Descrição</label>
                        <input
                          v-model="newStageDescription"
                          type="text"
                          placeholder="Descrição da etapa"
                          class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm placeholder:text-n-slate-10 focus:border-woot-500 focus:outline-none transition-colors"
                        />
                      </div>
                    </div>
                    <div class="flex justify-end">
                      <Button
                        :label="
                          editingStageIndex !== null
                            ? 'Atualizar etapa'
                            : 'Adicionar etapa'
                        "
                        icon="i-lucide-plus"
                        variant="smooth"
                        size="sm"
                        @click="addStage"
                      />
                    </div>
                  </div>
                </div>

                <!-- Stages List -->
                <div class="flex flex-col gap-2 max-h-48 overflow-y-auto">
                  <div
                    v-for="(stage, index) in customFunnel.stages"
                    :key="index"
                    class="flex items-center gap-3 p-3 rounded-lg bg-n-alpha-1"
                  >
                    <div
                      class="size-8 rounded-full flex-shrink-0"
                      :style="{ backgroundColor: stage.color }"
                    />
                    <span class="flex-1 text-sm text-n-slate-12">
                      {{ stage.label }}
                    </span>
                    <span
                      class="px-2 py-1 rounded text-xs font-mono text-n-slate-10 bg-n-alpha-2"
                    >
                      {{ stage.color }}
                    </span>
                    <button
                      class="size-7 rounded-lg flex items-center justify-center text-n-slate-11 hover:text-woot-500 hover:bg-n-alpha-2 transition-colors"
                      @click="editStage(index)"
                    >
                      <span class="i-lucide-pencil size-3.5" />
                    </button>
                    <button
                      class="size-7 rounded-lg flex items-center justify-center text-ruby-600 hover:bg-ruby-500/10 transition-colors"
                      @click="removeStage(index)"
                    >
                      <span class="i-lucide-trash-2 size-3.5" />
                    </button>
                  </div>

                  <div
                    v-if="customFunnel.stages.length === 0"
                    class="flex flex-col items-center justify-center py-8 text-n-slate-10"
                  >
                    <span class="i-lucide-layers size-8 opacity-50" />
                    <p class="text-sm mt-2">Nenhuma etapa adicionada</p>
                  </div>
                </div>
              </div>

              <!-- Right: Preview -->
              <div
                class="p-4 rounded-xl border-2 border-dashed border-n-weak bg-n-alpha-1 flex flex-col"
              >
                <h3 class="text-sm font-medium text-n-slate-11 mb-4">
                  Visualização
                </h3>

                <div v-if="customFunnel.name" class="mb-4">
                  <div class="flex items-center gap-2">
                    <div
                      class="size-8 rounded-lg bg-woot-500 flex items-center justify-center"
                    >
                      <span class="i-lucide-kanban size-4 text-white" />
                    </div>
                    <div>
                      <h4 class="text-sm font-medium text-n-slate-12">
                        {{ customFunnel.name }}
                      </h4>
                      <p
                        v-if="customFunnel.description"
                        class="text-xs text-n-slate-11"
                      >
                        {{ customFunnel.description }}
                      </p>
                    </div>
                  </div>
                </div>

                <div
                  v-if="customFunnel.stages.length > 0"
                  class="flex flex-col gap-2 flex-1"
                >
                  <div
                    v-for="(stage, index) in customFunnel.stages"
                    :key="index"
                    class="rounded-lg bg-n-solid-2 outline outline-1 outline-n-container overflow-hidden"
                  >
                    <div class="h-1.5" :style="{ backgroundColor: stage.color }" />
                    <div class="p-3">
                      <div class="flex items-center gap-2">
                        <div
                          class="size-2.5 rounded-full"
                          :style="{ backgroundColor: stage.color }"
                        />
                        <span class="text-sm font-medium text-n-slate-12">
                          {{ stage.label }}
                        </span>
                      </div>
                      <p v-if="stage.description" class="text-xs text-n-slate-11 mt-1">
                        {{ stage.description }}
                      </p>
                    </div>
                  </div>
                </div>

                <div
                  v-else
                  class="flex flex-col items-center justify-center flex-1 py-8 text-n-slate-10"
                >
                  <span class="i-lucide-kanban size-10 opacity-30" />
                  <p class="text-xs mt-2">Configure as etapas ao lado</p>
                </div>
              </div>
            </div>

            <!-- Templates Section -->
            <div class="mt-6 pt-6 border-t border-n-weak">
              <h3 class="text-sm font-medium text-n-slate-12 mb-1">
                Templates de Funil
              </h3>
              <p class="text-xs text-n-slate-11 mb-4">
                Escolha um template para começar rapidamente
              </p>

              <div class="grid grid-cols-3 gap-3">
                <div
                  v-for="template in funnelTemplates.slice(0, 9)"
                  :key="template.type"
                  class="p-4 rounded-xl cursor-pointer transition-all"
                  :class="
                    selectedTemplate === template.type
                      ? 'bg-woot-500/10 outline outline-2 outline-woot-500'
                      : 'bg-n-alpha-1 outline outline-1 outline-n-container hover:outline-n-slate-10'
                  "
                  @click="selectTemplate(template.type)"
                >
                  <div class="flex items-center gap-3 mb-3">
                    <span class="text-2xl">{{ template.icon }}</span>
                    <div class="flex-1 min-w-0">
                      <h4 class="text-sm font-medium text-n-slate-12 truncate">
                        {{ template.name }}
                      </h4>
                      <p class="text-xs text-n-slate-11 truncate">
                        {{ template.description }}
                      </p>
                    </div>
                  </div>
                  <div class="flex flex-wrap gap-1">
                    <span
                      v-for="(stage, key) in Object.values(template.stages).slice(0, 4)"
                      :key="key"
                      class="px-1.5 py-0.5 rounded text-[10px] font-medium text-white"
                      :style="{ backgroundColor: stage.color }"
                    >
                      {{ stage.label }}
                    </span>
                    <span
                      v-if="Object.values(template.stages).length > 4"
                      class="px-1.5 py-0.5 rounded text-[10px] font-medium bg-n-alpha-2 text-n-slate-11"
                    >
                      +{{ Object.values(template.stages).length - 4 }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div
            class="flex items-center justify-end gap-3 px-6 py-4 border-t border-n-weak"
          >
            <Button
              label="Cancelar"
              variant="ghost"
              color="slate"
              @click="
                showCreateModal = false;
                resetForm();
              "
            />
            <Button
              label="Criar Funil"
              :disabled="!customFunnel.name && !selectedTemplate"
              @click="handleCreateFunnel"
            />
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Edit Modal -->
    <Teleport to="body">
      <div
        v-if="showEditModal && selectedFunnel"
        class="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      >
        <div
          class="absolute inset-0 bg-n-alpha-black5 backdrop-blur-sm"
          @click="showEditModal = false"
        />

        <div
          class="relative w-full max-w-3xl max-h-[85vh] overflow-hidden rounded-2xl bg-n-solid-2 shadow-xl outline outline-1 outline-n-container flex flex-col"
        >
          <!-- Header -->
          <div
            class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
          >
            <h2 class="text-base font-medium text-n-slate-12">Editar Funil</h2>
            <button
              class="size-8 rounded-lg flex items-center justify-center text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
              @click="showEditModal = false"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </div>

          <!-- Content -->
          <div class="flex-1 overflow-y-auto p-6">
            <!-- Basic Info -->
            <div class="mb-6">
              <h3 class="text-sm font-medium text-n-slate-12 mb-4">
                Informações Básicas
              </h3>
              <div class="grid grid-cols-2 gap-4">
                <div class="flex flex-col gap-1.5">
                  <label class="text-sm font-medium text-n-slate-11">
                    Nome do Funil
                  </label>
                  <input
                    v-model="selectedFunnel.name"
                    type="text"
                    class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
                  />
                </div>
                <div class="flex flex-col gap-1.5">
                  <label class="text-sm font-medium text-n-slate-11">Tipo</label>
                  <input
                    v-model="selectedFunnel.funnel_type"
                    type="text"
                    disabled
                    class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-11 text-sm cursor-not-allowed"
                  />
                </div>
              </div>
              <div class="mt-4 flex flex-col gap-1.5">
                <label class="text-sm font-medium text-n-slate-11">Descrição</label>
                <textarea
                  v-model="selectedFunnel.description"
                  rows="2"
                  class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors resize-none"
                />
              </div>
            </div>

            <!-- Stages -->
            <div>
              <h3 class="text-sm font-medium text-n-slate-12 mb-4">
                Etapas do Funil
              </h3>

              <div class="flex flex-col gap-3">
                <div
                  v-for="(stage, key) in selectedFunnel.stages"
                  :key="key"
                  class="p-4 rounded-xl bg-n-alpha-1 outline outline-1 outline-n-container"
                >
                  <div class="grid grid-cols-12 gap-3 items-end">
                    <div class="col-span-1 flex flex-col gap-1.5">
                      <label class="text-xs font-medium text-n-slate-10">Pos.</label>
                      <input
                        v-model.number="stage.position"
                        type="number"
                        min="0"
                        class="w-full h-8 px-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm text-center focus:border-woot-500 focus:outline-none transition-colors"
                      />
                    </div>
                    <div class="col-span-3 flex flex-col gap-1.5">
                      <label class="text-xs font-medium text-n-slate-10">Nome</label>
                      <input
                        v-model="stage.label"
                        type="text"
                        class="w-full h-8 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
                      />
                    </div>
                    <div class="col-span-2 flex flex-col gap-1.5">
                      <label class="text-xs font-medium text-n-slate-10">Cor</label>
                      <div class="flex items-center gap-1.5">
                        <input
                          v-model="stage.color"
                          type="color"
                          class="size-8 rounded border border-n-weak cursor-pointer"
                        />
                        <input
                          v-model="stage.color"
                          type="text"
                          class="flex-1 h-8 px-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-xs font-mono focus:border-woot-500 focus:outline-none transition-colors"
                        />
                      </div>
                    </div>
                    <div class="col-span-4 flex flex-col gap-1.5">
                      <label class="text-xs font-medium text-n-slate-10">Descrição</label>
                      <input
                        v-model="stage.description"
                        type="text"
                        placeholder="Descrição da etapa"
                        class="w-full h-8 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm placeholder:text-n-slate-10 focus:border-woot-500 focus:outline-none transition-colors"
                      />
                    </div>
                    <div class="col-span-1 flex flex-col gap-1.5">
                      <label class="text-xs font-medium text-n-slate-10">SLA</label>
                      <input
                        v-model.number="stage.sla_hours"
                        type="number"
                        min="0"
                        placeholder="24"
                        class="w-full h-8 px-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm text-center focus:border-woot-500 focus:outline-none transition-colors"
                      />
                    </div>
                    <div class="col-span-1 flex justify-center">
                      <button
                        class="size-8 rounded-lg flex items-center justify-center text-ruby-600 hover:bg-ruby-500/10 transition-colors"
                        title="Remover etapa"
                        @click="removeStageFromSelectedFunnel(key)"
                      >
                        <span class="i-lucide-trash-2 size-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <Button
                label="Adicionar Nova Etapa"
                icon="i-lucide-plus"
                variant="smooth"
                size="sm"
                class="mt-4"
                @click="addStageToSelectedFunnel"
              />
            </div>
          </div>

          <!-- Footer -->
          <div
            class="flex items-center justify-end gap-3 px-6 py-4 border-t border-n-weak"
          >
            <Button
              label="Cancelar"
              variant="ghost"
              color="slate"
              @click="showEditModal = false"
            />
            <Button label="Salvar Alterações" @click="handleEditFunnel" />
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
